import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

import StickyFooter from '../components/StickyFooter';

interface Course {
  id: string;
  title: string;
  price: string;
  instructor: string;
  isActive: boolean;
}

interface Registration {
  id: string;
  courseId: string;
  courseName?: string; // Computed field for display
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  // Registrant information (person who made the registration)
  registrantFirstName?: string;
  registrantLastName?: string;
  registrantEmail?: string;
  registrantPhone?: string;
  // Organization details
  company?: string;
  website?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  // Separate invoicing details
  invoicingCompany?: string;
  invoicingStreet?: string;
  invoicingHouseNumber?: string;
  invoicingPostalCode?: string;
  invoicingCity?: string;
  // Additional information
  notes?: string;
  specialRequirements?: string;
  knownFrom?: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'alumni';
  registrationDate: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminRegistrationEditorProps {
  selectedRegistrationId?: string; // If provided, edit mode; if not, create mode
  onBack?: () => void;
  registrationId?: string; // If provided, edit mode; if not, create mode
  onSave?: (registration: Registration) => void;
  onCancel?: () => void;
}

const client = generateClient<Schema>({ authMode: 'userPool' });

const AdminRegistrationEditor: React.FC<AdminRegistrationEditorProps> = ({
  selectedRegistrationId: propSelectedRegistrationId,
  onBack,
  registrationId,
  onSave,
  onCancel
}) => {
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string>(propSelectedRegistrationId || registrationId || '');
  const [availableRegistrations, setAvailableRegistrations] = useState<{ id: string; name: string; courseName: string }[]>([]);
  
  const [formData, setFormData] = useState({
    courseId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Registrant information
    registrantFirstName: '',
    registrantLastName: '',
    registrantEmail: '',
    registrantPhone: '',
    // Organization details
    company: '',
    website: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    // Separate invoicing details
    invoicingCompany: '',
    invoicingStreet: '',
    invoicingHouseNumber: '',
    invoicingPostalCode: '',
    invoicingCity: '',
    // Additional information
    notes: '',
    specialRequirements: '',
    knownFrom: '',
    status: 'pending' as 'pending' | 'confirmed' | 'cancelled' | 'alumni'
  });
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);

  const isEditMode = Boolean(selectedRegistrationId);

  // Sample courses data
  const sampleCourses: Course[] = [
    {
      id: '1',
      title: 'AI Fundamentals Workshop',
      price: '€299',
      instructor: 'Dr. Sarah Johnson',
      isActive: true
    },
    {
      id: '2',
      title: 'Machine Learning Basics',
      price: '€399',
      instructor: 'Prof. Michael Chen',
      isActive: true
    },
    {
      id: '3',
      title: 'Data Analytics Masterclass',
      price: '€349',
      instructor: 'Anna Rodriguez',
      isActive: true
    },
    {
      id: '4',
      title: 'AI Strategy for Business',
      price: '€499',
      instructor: 'Thomas Mueller',
      isActive: true
    }
  ];

  useEffect(() => {
    // Load courses
    setCourses(sampleCourses);
    loadAvailableRegistrations();
    
    if (isEditMode && selectedRegistrationId) {
      loadRegistration(selectedRegistrationId);
    }
  }, [selectedRegistrationId, isEditMode]);

  useEffect(() => {
    if (propSelectedRegistrationId) {
      setSelectedRegistrationId(propSelectedRegistrationId);
      loadRegistration(propSelectedRegistrationId);
    }
  }, [propSelectedRegistrationId]);

  const loadAvailableRegistrations = async () => {
    try {
      console.log('🔍 Loading available registrations...');
      const { data } = await client.models.Registration.list();
      console.log('📝 Available registrations loaded:', data);
      
      const registrations = (data || []).map((registration: any) => ({
        id: registration.id,
        name: `${registration.firstName || 'Unknown'} ${registration.lastName || 'Person'}`,
        courseName: registration.courseName || 'Unknown Course' // This needs to be populated from Course relationship
      }));
      
      setAvailableRegistrations(registrations);
    } catch (error) {
      console.error('❌ Error loading available registrations:', error);
      setAvailableRegistrations([]);
    }
  };

  const handleRegistrationSelection = (registrationId: string) => {
    setSelectedRegistrationId(registrationId);
    if (registrationId) {
      loadRegistration(registrationId);
    } else {
      // Reset to create mode
      setFormData({
        courseId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        registrantFirstName: '',
        registrantLastName: '',
        registrantEmail: '',
        registrantPhone: '',
        company: '',
        website: '',
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        invoicingCompany: '',
        invoicingStreet: '',
        invoicingHouseNumber: '',
        invoicingPostalCode: '',
        invoicingCity: '',
        notes: '',
        specialRequirements: '',
        knownFrom: '',
        status: 'pending'
      });
    }
    setErrors({});
  };

  const loadRegistration = async (id: string) => {
    setLoading(true);
    try {
      console.log('🔍 Loading registration with ID:', id);
      const { data } = await client.models.Registration.get({ id });
      console.log('📝 Registration loaded:', data);
      
      if (data) {
        setFormData({
          courseId: data.courseId || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          registrantFirstName: (data as any).registrantFirstName || '',
          registrantLastName: (data as any).registrantLastName || '',
          registrantEmail: (data as any).registrantEmail || '',
          registrantPhone: (data as any).registrantPhone || '',
          company: data.company || '',
          website: (data as any).website || '',
          street: (data as any).street || '',
          houseNumber: (data as any).houseNumber || '',
          postalCode: (data as any).postalCode || '',
          city: (data as any).city || '',
          invoicingCompany: (data as any).invoicingCompany || '',
          invoicingStreet: (data as any).invoicingStreet || '',
          invoicingHouseNumber: (data as any).invoicingHouseNumber || '',
          invoicingPostalCode: (data as any).invoicingPostalCode || '',
          invoicingCity: (data as any).invoicingCity || '',
          notes: data.notes || '',
          specialRequirements: (data as any).specialRequirements || '',
          knownFrom: (data as any).knownFrom || '',
          status: (data.status as 'pending' | 'confirmed' | 'cancelled' | 'alumni') || 'pending'
        });
      }
    } catch (error) {
      console.error('❌ Error loading registration:', error);
      setMessage({ type: 'error', text: 'Failed to load registration. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only validate format constraints, no required fields
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (formData.registrantPhone && !isValidPhone(formData.registrantPhone)) {
      newErrors.registrantPhone = 'Please enter a valid registrant phone number';
    }

    if (formData.registrantEmail && !isValidEmail(formData.registrantEmail)) {
      newErrors.registrantEmail = 'Please enter a valid registrant email address';
    }

    // Validate postal codes (5 digits)
    if (formData.postalCode && !/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Postal code must be exactly 5 digits';
    }

    if (formData.invoicingPostalCode && !/^\d{5}$/.test(formData.invoicingPostalCode)) {
      newErrors.invoicingPostalCode = 'Invoicing postal code must be exactly 5 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string) => {
    // Basic phone validation - accepts various formats
    const phoneRegex = /^[\+]?[\d\s\-\(\)]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (saving) {
      console.warn('⚠️ Save already in progress, ignoring duplicate submission');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      console.log('🔧 Registration submission started');
      
      const selectedCourse = courses.find(c => c.id === formData.courseId);
      
      // Prepare registration data - EXCLUDE registrationDate (immutable after creation)
      const registrationData = {
        courseId: formData.courseId.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        notes: formData.notes.trim(),
        status: formData.status,
      };
      
      console.log('📝 Creating/updating registration with data:', registrationData);

      let result;
      if (isEditMode && selectedRegistrationId) {
        // Update existing registration (DO NOT modify registrationDate)
        console.log('🔄 Updating existing registration - registrationDate will remain unchanged');
        result = await client.models.Registration.update({
          id: selectedRegistrationId,
          ...registrationData
        });
        console.log('✅ Registration update result:', result);
      } else {
        // Create new registration (SET registrationDate to current time)
        console.log('✨ Creating new registration - setting registrationDate to now');
        result = await client.models.Registration.create({
          ...registrationData,
          registrationDate: new Date().toISOString()
        });
        console.log('✅ Registration creation result:', result);
      }

      if (result?.data) {
        const savedRegistration: Registration = {
          id: result.data.id,
          courseId: result.data.courseId || '',
          courseName: result.data.courseName || '',
          firstName: result.data.firstName || '',
          lastName: result.data.lastName || '',
          email: result.data.email || '',
          phone: result.data.phone || '',
          company: result.data.company || '',
          notes: result.data.notes || '',
          status: (result.data.status as 'pending' | 'confirmed' | 'cancelled') || 'pending',
          registrationDate: result.data.registrationDate || new Date().toISOString().split('T')[0],
          createdAt: result.data.createdAt || new Date().toISOString(),
          updatedAt: result.data.updatedAt || new Date().toISOString()
        };

        if (onSave) {
          onSave(savedRegistration);
        }

        if (!isEditMode) {
          // Reset form for new registration
          setFormData({
            courseId: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            registrantFirstName: '',
            registrantLastName: '',
            registrantEmail: '',
            registrantPhone: '',
            company: '',
            website: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            invoicingCompany: '',
            invoicingStreet: '',
            invoicingHouseNumber: '',
            invoicingPostalCode: '',
            invoicingCity: '',
            notes: '',
            specialRequirements: '',
            knownFrom: '',
            status: 'pending'
          });
          // Reload available registrations list
          await loadAvailableRegistrations();
        }

        setMessage({ type: 'success', text: `Registration ${isEditMode ? 'updated' : 'created'} successfully!` });
      } else {
        throw new Error('No data returned from server');
      }
    } catch (err: any) {
      console.error('❌ Failed to save registration:', {
        name: (err as any)?.name,
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        errors: (err as any)?.errors
      });
      
      let errorMessage = 'Failed to save registration. Please try again.';
      if ((err as any)?.errors?.[0]?.message) {
        errorMessage = `GraphQL error: ${(err as any).errors[0].message}`;
      } else if ((err as any)?.message) {
        errorMessage = `Error: ${(err as any).message}`;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Always reset to blank values
    setFormData({
      courseId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      registrantFirstName: '',
      registrantLastName: '',
      registrantEmail: '',
      registrantPhone: '',
      company: '',
      website: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      invoicingCompany: '',
      invoicingStreet: '',
      invoicingHouseNumber: '',
      invoicingPostalCode: '',
      invoicingCity: '',
      notes: '',
      specialRequirements: '',
      knownFrom: '',
      status: 'pending'
    });
    setErrors({});
  };

  // Inject selector into header
  React.useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="registrationSelect"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 250px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
        >
          <option value="" ${selectedRegistrationId === '' ? 'selected' : ''}>Create New Registration</option>
          ${availableRegistrations.map(registration => 
            `<option value="${registration.id}" ${selectedRegistrationId === registration.id ? 'selected' : ''}>${registration.name} - ${registration.courseName}</option>`
          ).join('')}
        </select>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          handleRegistrationSelection((e.target as HTMLSelectElement).value);
        });
      }
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [selectedRegistrationId, availableRegistrations]);

  if (loading) {
    return (
      <div className="admin-form">
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div>Loading registration...</div>
        </div>
      </div>
    );
  }

  const selectedCourse = courses.find(c => c.id === formData.courseId);

  return (
    <div className="admin-form admin-editor-content">

      
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="courseId">Course</label>
            <select
              id="courseId"
              value={formData.courseId}
              onChange={(e) => handleInputChange('courseId', e.target.value)}
              className={errors.courseId ? 'error' : ''}
            >
              <option value="">Select a course...</option>
              {courses.filter(course => course.isActive).map(course => (
                <option key={course.id} value={course.id}>
                  {course.title} - {course.price} (by {course.instructor})
                </option>
              ))}
            </select>
            {errors.courseId && (
              <div className="error-message">{errors.courseId}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="status">Registration Status</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Paid</option>
              <option value="cancelled">Cancelled</option>
              <option value="alumni">Alumni</option>
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Enter participant's first name"
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && (
              <div className="error-message">{errors.firstName}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Enter participant's last name"
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && (
              <div className="error-message">{errors.lastName}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="participant@example.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="phone">Phone Number</label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+49 123 456789"
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && (
              <div className="error-message">{errors.phone}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Company name (optional)"
            />
          </div>
        </div>

        {/* Registrant Information Section */}
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#374151' }}>Registrant Information</h3>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="registrantFirstName">Registrant First Name</label>
            <input
              id="registrantFirstName"
              type="text"
              value={formData.registrantFirstName}
              onChange={(e) => handleInputChange('registrantFirstName', e.target.value)}
              placeholder="Person who made the registration"
            />
          </div>

          <div className="form-field">
            <label htmlFor="registrantLastName">Registrant Last Name</label>
            <input
              id="registrantLastName"
              type="text"
              value={formData.registrantLastName}
              onChange={(e) => handleInputChange('registrantLastName', e.target.value)}
              placeholder="Person who made the registration"
            />
          </div>

          <div className="form-field">
            <label htmlFor="registrantEmail">Registrant Email</label>
            <input
              id="registrantEmail"
              type="email"
              value={formData.registrantEmail}
              onChange={(e) => handleInputChange('registrantEmail', e.target.value)}
              placeholder="registrant@example.com"
              className={errors.registrantEmail ? 'error' : ''}
            />
            {errors.registrantEmail && (
              <div className="error-message">{errors.registrantEmail}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="registrantPhone">Registrant Phone</label>
            <input
              id="registrantPhone"
              type="tel"
              value={formData.registrantPhone}
              onChange={(e) => handleInputChange('registrantPhone', e.target.value)}
              placeholder="+49 123 456789"
              className={errors.registrantPhone ? 'error' : ''}
            />
            {errors.registrantPhone && (
              <div className="error-message">{errors.registrantPhone}</div>
            )}
          </div>
        </div>

        {/* Organization Details Section */}
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#374151' }}>Organization Details</h3>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>

          <div className="form-field">
            <label htmlFor="street">Street</label>
            <input
              id="street"
              type="text"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              placeholder="Street name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="houseNumber">House Number</label>
            <input
              id="houseNumber"
              type="text"
              value={formData.houseNumber}
              onChange={(e) => handleInputChange('houseNumber', e.target.value)}
              placeholder="123"
            />
          </div>

          <div className="form-field">
            <label htmlFor="postalCode">Postal Code</label>
            <input
              id="postalCode"
              type="text"
              value={formData.postalCode}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              placeholder="12345"
              maxLength={5}
              className={errors.postalCode ? 'error' : ''}
            />
            {errors.postalCode && (
              <div className="error-message">{errors.postalCode}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="City name"
            />
          </div>
        </div>

        {/* Invoicing Details Section */}
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#374151' }}>Invoicing Details</h3>
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="invoicingCompany">Invoicing Company</label>
            <input
              id="invoicingCompany"
              type="text"
              value={formData.invoicingCompany}
              onChange={(e) => handleInputChange('invoicingCompany', e.target.value)}
              placeholder="Company for invoicing (if different)"
            />
          </div>

          <div className="form-field">
            <label htmlFor="invoicingStreet">Invoicing Street</label>
            <input
              id="invoicingStreet"
              type="text"
              value={formData.invoicingStreet}
              onChange={(e) => handleInputChange('invoicingStreet', e.target.value)}
              placeholder="Invoicing street"
            />
          </div>

          <div className="form-field">
            <label htmlFor="invoicingHouseNumber">Invoicing House Number</label>
            <input
              id="invoicingHouseNumber"
              type="text"
              value={formData.invoicingHouseNumber}
              onChange={(e) => handleInputChange('invoicingHouseNumber', e.target.value)}
              placeholder="123"
            />
          </div>

          <div className="form-field">
            <label htmlFor="invoicingPostalCode">Invoicing Postal Code</label>
            <input
              id="invoicingPostalCode"
              type="text"
              value={formData.invoicingPostalCode}
              onChange={(e) => handleInputChange('invoicingPostalCode', e.target.value)}
              placeholder="12345"
              maxLength={5}
              className={errors.invoicingPostalCode ? 'error' : ''}
            />
            {errors.invoicingPostalCode && (
              <div className="error-message">{errors.invoicingPostalCode}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="invoicingCity">Invoicing City</label>
            <input
              id="invoicingCity"
              type="text"
              value={formData.invoicingCity}
              onChange={(e) => handleInputChange('invoicingCity', e.target.value)}
              placeholder="Invoicing city"
            />
          </div>
        </div>

        {/* Additional Information Section */}
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#374151' }}>Additional Information</h3>
        <div className="form-field">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="General notes or comments..."
            rows={3}
          />
        </div>

        <div className="form-field">
          <label htmlFor="specialRequirements">Special Requirements</label>
          <textarea
            id="specialRequirements"
            value={formData.specialRequirements}
            onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
            placeholder="Accessibility needs, dietary requirements, etc..."
            rows={3}
          />
        </div>

        <div className="form-field">
          <label htmlFor="knownFrom">Known From</label>
          <input
            type="text"
            id="knownFrom"
            value={formData.knownFrom}
            onChange={(e) => handleInputChange('knownFrom', e.target.value)}
            placeholder="How did the user hear about us and this course?"
          />
        </div>

        {selectedCourse && (
          <div className="form-field">
            <label>Selected Course Details</label>
            <div style={{ 
              padding: '12px', 
              background: '#f0f9ff', 
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              <strong>{selectedCourse.title}</strong><br />
              <span style={{ color: '#6b7280' }}>
                Price: {selectedCourse.price} | Instructor: {selectedCourse.instructor}
              </span>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="admin-btn secondary"
            disabled={saving}
          >
            Reset
          </button>
        </div>
      </form>
      
      <StickyFooter
        onSave={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }}
        saving={saving}
        message={message}
        onMessageClose={() => setMessage(null)}
      />
    </div>
  );
};

export default AdminRegistrationEditor;
